import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './TetrisGame.css';

const COLS = 10;
const ROWS = 20;
const BUFFER_ROWS = 4;
const TOTAL_ROWS = ROWS + BUFFER_ROWS;
const CELL = 30;
const BOARD_W = COLS * CELL;
const BOARD_H = ROWS * CELL;

const PIECES = {
  I: { color: '#06b6d4', cells: [
    [[1,0],[1,1],[1,2],[1,3]],
    [[0,2],[1,2],[2,2],[3,2]],
    [[2,0],[2,1],[2,2],[2,3]],
    [[0,1],[1,1],[2,1],[3,1]],
  ]},
  O: { color: '#eab308', cells: [
    [[0,0],[0,1],[1,0],[1,1]],
    [[0,0],[0,1],[1,0],[1,1]],
    [[0,0],[0,1],[1,0],[1,1]],
    [[0,0],[0,1],[1,0],[1,1]],
  ]},
  T: { color: '#a855f7', cells: [
    [[0,1],[1,0],[1,1],[1,2]],
    [[0,1],[1,1],[1,2],[2,1]],
    [[1,0],[1,1],[1,2],[2,1]],
    [[0,1],[1,0],[1,1],[2,1]],
  ]},
  S: { color: '#22c55e', cells: [
    [[0,1],[0,2],[1,0],[1,1]],
    [[0,0],[1,0],[1,1],[2,1]],
    [[1,1],[1,2],[2,0],[2,1]],
    [[0,1],[1,0],[1,1],[2,0]],
  ]},
  Z: { color: '#ef4444', cells: [
    [[0,0],[0,1],[1,1],[1,2]],
    [[0,2],[1,1],[1,2],[2,1]],
    [[1,0],[1,1],[2,1],[2,2]],
    [[0,1],[1,0],[1,1],[2,0]],
  ]},
  J: { color: '#3b82f6', cells: [
    [[0,0],[1,0],[1,1],[1,2]],
    [[0,1],[0,2],[1,1],[2,1]],
    [[1,0],[1,1],[1,2],[2,2]],
    [[0,1],[1,1],[2,0],[2,1]],
  ]},
  L: { color: '#f97316', cells: [
    [[0,2],[1,0],[1,1],[1,2]],
    [[0,1],[1,1],[2,1],[2,2]],
    [[1,0],[1,1],[1,2],[2,0]],
    [[0,0],[0,1],[1,1],[2,1]],
  ]},
};

const PIECE_TYPES = Object.keys(PIECES);

const ROTATION_NAMES = ['0', 'R', '2', 'L'];

const KICKS_JLSTZ = {
  '0>R': [[0,0],[-1,0],[-1,-1],[0,2],[-1,2]],
  'R>2': [[0,0],[1,0],[1,1],[0,-2],[1,-2]],
  '2>L': [[0,0],[1,0],[1,-1],[0,2],[1,2]],
  'L>0': [[0,0],[-1,0],[-1,1],[0,-2],[-1,-2]],
  '0>L': [[0,0],[1,0],[1,-1],[0,2],[1,2]],
  'L>2': [[0,0],[-1,0],[-1,1],[0,-2],[-1,-2]],
  '2>R': [[0,0],[-1,0],[-1,-1],[0,2],[-1,2]],
  'R>0': [[0,0],[1,0],[1,1],[0,-2],[1,-2]],
};

const KICKS_I = {
  '0>R': [[0,0],[-2,0],[1,0],[-2,1],[1,-2]],
  'R>2': [[0,0],[-1,0],[2,0],[-1,-2],[2,1]],
  '2>L': [[0,0],[2,0],[-1,0],[2,-1],[-1,2]],
  'L>0': [[0,0],[1,0],[-2,0],[1,2],[-2,-1]],
  '0>L': [[0,0],[-1,0],[2,0],[-1,-2],[2,1]],
  'L>2': [[0,0],[-2,0],[1,0],[-2,1],[1,-2]],
  '2>R': [[0,0],[1,0],[-2,0],[1,2],[-2,-1]],
  'R>0': [[0,0],[2,0],[-1,0],[2,-1],[-1,2]],
};

function createBoard() {
  return Array.from({ length: TOTAL_ROWS }, () => Array(COLS).fill(0));
}

function shuffleBag() {
  const bag = [...PIECE_TYPES];
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return bag;
}

function getKickTable(type) {
  if (type === 'I') return KICKS_I;
  if (type === 'O') return null;
  return KICKS_JLSTZ;
}

function getPieceCells(type, rotation) {
  return PIECES[type].cells[rotation];
}

function isValid(board, type, rotation, row, col) {
  const cells = getPieceCells(type, rotation);
  for (const [dr, dc] of cells) {
    const r = row + dr;
    const c = col + dc;
    if (c < 0 || c >= COLS || r < 0 || r >= TOTAL_ROWS) return false;
    if (board[r][c] !== 0) return false;
  }
  return true;
}

function tryRotate(board, type, currentRotation, row, col, direction) {
  const newRotation = (currentRotation + direction + 4) % 4;
  const fromName = ROTATION_NAMES[currentRotation];
  const toName = ROTATION_NAMES[newRotation];
  const kickTable = getKickTable(type);

  if (!kickTable) {
    return isValid(board, type, newRotation, row, col)
      ? { rotation: newRotation, row, col }
      : null;
  }

  const key = `${fromName}>${toName}`;
  const kicks = kickTable[key];
  if (!kicks) return null;

  for (const [dx, dy] of kicks) {
    const newRow = row - dy;
    const newCol = col + dx;
    if (isValid(board, type, newRotation, newRow, newCol)) {
      return { rotation: newRotation, row: newRow, col: newCol };
    }
  }
  return null;
}

function getGhostRow(board, type, rotation, row, col) {
  let ghostRow = row;
  while (isValid(board, type, rotation, ghostRow + 1, col)) {
    ghostRow += 1;
  }
  return ghostRow;
}

function lockPiece(board, type, rotation, row, col) {
  const cells = getPieceCells(type, rotation);
  for (const [dr, dc] of cells) {
    board[row + dr][col + dc] = type;
  }
  return board;
}

function clearLines(board) {
  let linesCleared = 0;
  for (let r = TOTAL_ROWS - 1; r >= BUFFER_ROWS; r--) {
    if (board[r].every(cell => cell !== 0)) {
      board.splice(r, 1);
      board.unshift(Array(COLS).fill(0));
      linesCleared++;
      r++;
    }
  }
  return { board, linesCleared };
}

function spawnPiece(type) {
  return {
    type,
    rotation: 0,
    row: BUFFER_ROWS - 2,
    col: type === 'O' ? 4 : 3,
  };
}

const LINE_CLEAR_POINTS = { 1: 100, 2: 300, 3: 500, 4: 800 };

function calculateLineClearScore(linesCleared, level, lastClearWasDifficult) {
  if (linesCleared === 0) return { points: 0, isDifficult: false };

  const base = LINE_CLEAR_POINTS[linesCleared] || 0;
  const isDifficult = linesCleared === 4;
  const backToBack = isDifficult && lastClearWasDifficult;
  const multiplier = backToBack ? 1.5 : 1;

  return {
    points: Math.floor(base * level * multiplier),
    isDifficult,
  };
}

function getDropInterval(level) {
  const speeds = [1000, 793, 618, 473, 355, 262, 190, 135, 94, 64];
  return speeds[Math.min(level - 1, speeds.length - 1)];
}

export default function TetrisGame({
  onGameOver,
  isGenerating,
  generationSteps = [],
  effectiveStatusIndex = 0,
  onViewResults,
  articleReady = false,
  articleFailed = false,
  statusLog = [],
}) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const logEndRef = useRef(null);
  const rafRef = useRef(null);

  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);

  const gameRef = useRef({
    board: createBoard(),
    current: null,
    bag: [],
    nextPiece: null,
    score: 0,
    level: 1,
    lines: 0,
    lastClearWasDifficult: false,
    gameOver: false,
    lastDropTime: 0,
    lockDelay: 0,
    lockMoveResets: 0,
    isLocking: false,
    dasDirection: null,
    dasTimer: 0,
    dasActive: false,
  });

  const fetchLB = useCallback(async () => {
    try {
      const res = await api.get('/api/game/leaderboard?game_type=tetris');
      setLeaderboard(res.data?.leaderboard || []);
      setCurrentUserRank(res.data?.current_user || null);
    } catch {}
  }, []);

  useEffect(() => { fetchLB(); }, [fetchLB]);

  function nextFromBag() {
    const g = gameRef.current;
    if (g.bag.length === 0) g.bag = shuffleBag();
    return g.bag.shift();
  }

  function performLock() {
    const g = gameRef.current;
    if (!g.current) return;
    lockPiece(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col);
    const { linesCleared } = clearLines(g.board);
    if (linesCleared > 0) {
      const { points, isDifficult } = calculateLineClearScore(
        linesCleared, g.level, g.lastClearWasDifficult
      );
      g.score += points;
      g.lines += linesCleared;
      const prevLevel = g.level;
      g.level = Math.floor(g.lines / 10) + 1;
      g.lastClearWasDifficult = isDifficult;
      if (g.level !== prevLevel) g.lastDropTime = performance.now();
      setScore(g.score);
      setLevel(g.level);
      setLines(g.lines);
    }
    g.isLocking = false;
    g.lockMoveResets = 0;
    spawnNext();
  }

  function spawnNext() {
    const g = gameRef.current;
    g.current = spawnPiece(g.nextPiece);
    g.nextPiece = nextFromBag();
    if (!isValid(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col)) {
      g.gameOver = true;
      setGameOver(true);
      draw();
      onGameOver?.(g.score, { level: g.level, lines: g.lines });
      fetchLB();
    }
  }

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const g = gameRef.current;

    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, BOARD_W, BOARD_H);

    ctx.strokeStyle = 'rgba(99,102,241,0.08)';
    ctx.lineWidth = 0.5;
    for (let c = 0; c <= COLS; c++) {
      ctx.beginPath(); ctx.moveTo(c * CELL, 0); ctx.lineTo(c * CELL, BOARD_H); ctx.stroke();
    }
    for (let r = 0; r <= ROWS; r++) {
      ctx.beginPath(); ctx.moveTo(0, r * CELL); ctx.lineTo(BOARD_W, r * CELL); ctx.stroke();
    }

    ctx.shadowBlur = 0;
    for (let r = BUFFER_ROWS; r < TOTAL_ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (g.board[r][c] !== 0) {
          const color = PIECES[g.board[r][c]].color;
          ctx.fillStyle = color;
          ctx.shadowColor = color;
          ctx.shadowBlur = 4;
          ctx.fillRect(c * CELL, (r - BUFFER_ROWS) * CELL, CELL - 1, CELL - 1);
        }
      }
    }
    ctx.shadowBlur = 0;

    if (g.current) {
      const { type, rotation, row, col } = g.current;
      const color = PIECES[type].color;
      const cells = getPieceCells(type, rotation);

      const ghostRow = getGhostRow(g.board, type, rotation, row, col);
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = color;
      for (const [dr, dc] of cells) {
        const vy = ghostRow + dr - BUFFER_ROWS;
        if (vy >= 0) {
          ctx.fillRect((col + dc) * CELL, vy * CELL, CELL - 1, CELL - 1);
        }
      }
      ctx.globalAlpha = 1.0;

      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      for (const [dr, dc] of cells) {
        const vy = row + dr - BUFFER_ROWS;
        if (vy >= 0) {
          ctx.fillRect((col + dc) * CELL, vy * CELL, CELL - 1, CELL - 1);
        }
      }
      ctx.shadowBlur = 0;
    }

    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, BOARD_W - 2, BOARD_H - 2);
  }, []);

  const drawPreview = useCallback(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const g = gameRef.current;

    ctx.fillStyle = '#0e0e15';
    ctx.fillRect(0, 0, 120, 120);

    if (!g.nextPiece) return;
    const cells = getPieceCells(g.nextPiece, 0);
    const color = PIECES[g.nextPiece].color;

    let minR = 4, maxR = 0, minC = 4, maxC = 0;
    for (const [dr, dc] of cells) {
      minR = Math.min(minR, dr); maxR = Math.max(maxR, dr);
      minC = Math.min(minC, dc); maxC = Math.max(maxC, dc);
    }
    const pieceH = maxR - minR + 1;
    const pieceW = maxC - minC + 1;
    const offsetX = (120 - pieceW * CELL) / 2 - minC * CELL;
    const offsetY = (120 - pieceH * CELL) / 2 - minR * CELL;

    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;
    for (const [dr, dc] of cells) {
      ctx.fillRect(offsetX + dc * CELL, offsetY + dr * CELL, CELL - 1, CELL - 1);
    }
    ctx.shadowBlur = 0;
  }, []);

  const gameLoop = useCallback((timestamp) => {
    const g = gameRef.current;
    if (g.gameOver) return;

    if (g.dasDirection && g.current) {
      const dc = g.dasDirection === 'left' ? -1 : 1;
      if (!g.dasActive) {
        if (timestamp - g.dasTimer >= 170) {
          g.dasActive = true;
          g.dasTimer = timestamp;
          if (isValid(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col + dc)) {
            g.current.col += dc;
            if (g.isLocking && g.lockMoveResets < 15) { g.lockMoveResets++; g.lockDelay = timestamp; }
          }
        }
      } else if (timestamp - g.dasTimer >= 50) {
        g.dasTimer = timestamp;
        if (isValid(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col + dc)) {
          g.current.col += dc;
          if (g.isLocking && g.lockMoveResets < 15) { g.lockMoveResets++; g.lockDelay = timestamp; }
        }
      }
    }

    if (!g.lastDropTime) g.lastDropTime = timestamp;
    const elapsed = timestamp - g.lastDropTime;
    const interval = getDropInterval(g.level);

    if (g.isLocking) {
      if (timestamp - g.lockDelay >= 500) {
        performLock();
      }
    } else if (elapsed >= interval) {
      if (isValid(g.board, g.current.type, g.current.rotation, g.current.row + 1, g.current.col)) {
        g.current.row += 1;
        g.lastDropTime = timestamp;
      } else {
        g.isLocking = true;
        g.lockDelay = timestamp;
        g.lockMoveResets = 0;
      }
    }

    draw();
    drawPreview();
    rafRef.current = requestAnimationFrame(gameLoop);
  }, [draw, drawPreview]);

  function reset() {
    const g = gameRef.current;
    g.board = createBoard();
    g.bag = [];
    g.current = null;
    g.nextPiece = null;
    g.score = 0;
    g.level = 1;
    g.lines = 0;
    g.lastClearWasDifficult = false;
    g.gameOver = false;
    g.lastDropTime = 0;
    g.lockDelay = 0;
    g.lockMoveResets = 0;
    g.isLocking = false;
    g.dasDirection = null;
    g.dasTimer = 0;
    g.dasActive = false;
    setScore(0);
    setLevel(1);
    setLines(0);
    setGameOver(false);
  }

  useEffect(() => {
    if (!started || gameOver) {
      draw();
      drawPreview();
      return;
    }
    const g = gameRef.current;
    if (g.current === null) {
      g.nextPiece = nextFromBag();
      spawnNext();
    }
    g.lastDropTime = 0;
    rafRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, gameOver]);

  useEffect(() => {
    const handler = (e) => {
      const g = gameRef.current;

      if (e.key === ' ' && g.gameOver) {
        e.preventDefault();
        reset();
        setStarted(true);
        return;
      }

      const moveKeys = ['ArrowLeft','ArrowRight','ArrowDown','ArrowUp','a','A','d','D','s','S','w','W','z','Z',' '];
      if (moveKeys.includes(e.key) && !started) {
        setStarted(true);
      }

      if (!g.current || g.gameOver) return;

      const resetLockIfNeeded = () => {
        if (g.isLocking && g.lockMoveResets < 15) {
          g.lockMoveResets++;
          g.lockDelay = performance.now();
        }
      };

      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A': {
          if (e.repeat) break;
          const dc = -1;
          if (isValid(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col + dc)) {
            g.current.col += dc;
            resetLockIfNeeded();
            if (!isValid(g.board, g.current.type, g.current.rotation, g.current.row + 1, g.current.col)) {
              if (!g.isLocking) { g.isLocking = true; g.lockDelay = performance.now(); g.lockMoveResets = 0; }
            } else if (g.isLocking) {
              g.isLocking = false;
            }
          }
          g.dasDirection = 'left';
          g.dasTimer = performance.now();
          g.dasActive = false;
          e.preventDefault();
          break;
        }
        case 'ArrowRight':
        case 'd':
        case 'D': {
          if (e.repeat) break;
          const dc = 1;
          if (isValid(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col + dc)) {
            g.current.col += dc;
            resetLockIfNeeded();
            if (!isValid(g.board, g.current.type, g.current.rotation, g.current.row + 1, g.current.col)) {
              if (!g.isLocking) { g.isLocking = true; g.lockDelay = performance.now(); g.lockMoveResets = 0; }
            } else if (g.isLocking) {
              g.isLocking = false;
            }
          }
          g.dasDirection = 'right';
          g.dasTimer = performance.now();
          g.dasActive = false;
          e.preventDefault();
          break;
        }
        case 'ArrowDown':
        case 's':
        case 'S':
          if (isValid(g.board, g.current.type, g.current.rotation, g.current.row + 1, g.current.col)) {
            g.current.row += 1;
            g.score += 1;
            setScore(g.score);
            g.lastDropTime = performance.now();
            if (!isValid(g.board, g.current.type, g.current.rotation, g.current.row + 1, g.current.col)) {
              if (!g.isLocking) { g.isLocking = true; g.lockDelay = performance.now(); g.lockMoveResets = 0; }
            } else if (g.isLocking) {
              g.isLocking = false;
            }
          }
          e.preventDefault();
          break;
        case 'ArrowUp':
        case 'w':
        case 'W': {
          const result = tryRotate(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col, 1);
          if (result) {
            g.current.rotation = result.rotation;
            g.current.row = result.row;
            g.current.col = result.col;
            resetLockIfNeeded();
          }
          e.preventDefault();
          break;
        }
        case 'z':
        case 'Z': {
          const result = tryRotate(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col, -1);
          if (result) {
            g.current.rotation = result.rotation;
            g.current.row = result.row;
            g.current.col = result.col;
            resetLockIfNeeded();
          }
          e.preventDefault();
          break;
        }
        case ' ': {
          e.preventDefault();
          const ghostRow = getGhostRow(g.board, g.current.type, g.current.rotation, g.current.row, g.current.col);
          g.score += 2 * (ghostRow - g.current.row);
          setScore(g.score);
          g.current.row = ghostRow;
          performLock();
          break;
        }
        default:
          break;
      }
    };
    const keyupHandler = (e) => {
      const g = gameRef.current;
      const leftKeys = ['ArrowLeft', 'a', 'A'];
      const rightKeys = ['ArrowRight', 'd', 'D'];
      if (leftKeys.includes(e.key) && g.dasDirection === 'left') {
        g.dasDirection = null;
        g.dasActive = false;
      } else if (rightKeys.includes(e.key) && g.dasDirection === 'right') {
        g.dasDirection = null;
        g.dasActive = false;
      }
    };

    window.addEventListener('keydown', handler);
    window.addEventListener('keyup', keyupHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('keyup', keyupHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [statusLog.length]);

  return (
    <div className="tetris-game-container">
      <div className={`tetris-status-banner ${articleReady ? 'article-ready' : ''} ${articleFailed ? 'article-failed' : ''}`}
           onClick={(articleReady || articleFailed) ? onViewResults : undefined}
           role={(articleReady || articleFailed) ? 'button' : undefined}
           tabIndex={(articleReady || articleFailed) ? 0 : undefined}>
        {articleReady ? (
          <span className="tetris-banner-ready">Article ready — click to view results</span>
        ) : articleFailed ? (
          <span className="tetris-banner-failed">Generation failed — click to view details</span>
        ) : (
          <div className="tetris-banner-steps">
            {generationSteps.filter(s => s.key !== 'queued' && s.key !== 'failed').map((step) => {
              const realIdx = generationSteps.findIndex(s => s.key === step.key);
              const isPast = effectiveStatusIndex > realIdx;
              const isActive = effectiveStatusIndex === realIdx;
              return (
                <span key={step.key} className={`tetris-step-chip ${isPast ? 'past' : ''} ${isActive ? 'active' : ''}`}>
                  {isPast ? '✓ ' : ''}{step.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="tetris-main">
        <div className="tetris-canvas-wrapper" onClick={() => { if (!started) setStarted(true); }}>
          <canvas ref={canvasRef} width={BOARD_W} height={BOARD_H} />
          {!started && !gameOver && (
            <div className="tetris-overlay">
              <div className="tetris-overlay-content">
                <p style={{ fontSize: '1.2rem', color: '#06b6d4' }}>Click or press any key to play</p>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>
                  ← → Move &nbsp; ↑ Rotate &nbsp; ↓ Soft Drop &nbsp; Space Hard Drop
                </p>
              </div>
            </div>
          )}
          {gameOver && (
            <div className="tetris-overlay">
              <div className="tetris-overlay-content">
                <h2>Game Over</h2>
                <p className="tetris-final-score">Score: {score}</p>
                <p className="tetris-final-level">Level {level} — {lines} lines</p>
                <p className="tetris-restart-hint">Press Space to restart</p>
              </div>
            </div>
          )}
        </div>

        <div className="tetris-side-panel">
          <div className="tetris-score-block">
            <div className="tetris-score-row">
              <span className="tetris-score-label">SCORE</span>
              <span className="tetris-score-value">{score}</span>
            </div>
            <div className="tetris-score-row">
              <span className="tetris-score-label">LEVEL</span>
              <span className="tetris-score-value tetris-level-value">{level}</span>
            </div>
            <div className="tetris-score-row">
              <span className="tetris-score-label">LINES</span>
              <span className="tetris-score-value tetris-lines-value">{lines}</span>
            </div>
          </div>

          <div className="tetris-next-piece">
            <h4 className="tetris-panel-title">Next</h4>
            <canvas ref={previewCanvasRef} width={120} height={120} />
          </div>

          <div className="tetris-live-log">
            <h4 className="tetris-panel-title">Generation Log</h4>
            <div className="tetris-log-entries">
              {statusLog.map((entry, i) => (
                <div key={i} className="tetris-log-entry">
                  <span className="tetris-log-time">
                    {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className="tetris-log-msg">{entry.message}</span>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          </div>

          <div className="tetris-leaderboard">
            <h4 className="tetris-panel-title">Leaderboard</h4>
            {leaderboard.length > 0 ? (
              <table className="tetris-lb-table">
                <thead><tr><th>#</th><th>Player</th><th>Score</th></tr></thead>
                <tbody>
                  {leaderboard.map((row, i) => {
                    const isMe = user?.name && row.username === user.name;
                    return (
                      <tr key={i} className={`${row.rank <= 3 ? 'tetris-lb-top' : ''} ${isMe ? 'tetris-lb-me' : ''}`}>
                        <td>{row.rank}</td>
                        <td>{row.username}{isMe ? ' (you)' : ''}</td>
                        <td>{row.score}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="tetris-lb-empty">No scores yet</p>
            )}
            {currentUserRank && (
              <div className="tetris-lb-you">
                #{currentUserRank.rank} {currentUserRank.username} (you) — {currentUserRank.score}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
